import { type Atom, batch } from "@tanstack/react-store";
import type { RowData } from "@tanstack/react-table";
import { isEqual } from "@common/helpers/comparison";
import { getVariantValue } from "../cells/cellVariant";
import { isDisplayColumnKind } from "../columns/columnLayout";
import { resolveColumnMeta } from "../columns/columnKinds";
import type {
    DataGridColumnInstance,
    DataGridPin,
    DataGridTable,
} from "../columns/types";
import type { DataGridColumnState } from "../types";

/** One value column as the settings panel lists it. */
export interface ColumnSettingsRow {
    id: string;
    name: string;
    isVisible: boolean;
    canHide: boolean;
    pin: DataGridPin | undefined;
}

/** The rows sharing one pin; reordering stays inside a zone. */
export interface ColumnSettingsZone {
    pin: DataGridPin | undefined;
    rows: ColumnSettingsRow[];
}

const ZONE_PINS: ReadonlyArray<DataGridPin | undefined> = [
    "start",
    undefined,
    "end",
];

const getPin = <TData extends RowData>(
    column: DataGridColumnInstance<TData>,
): DataGridPin | undefined => column.getIsPinned() || undefined;

/** Whether the column holds values (not select, expand, actions and the like). */
export const isValueColumn = <TData extends RowData>(
    column: DataGridColumnInstance<TData>,
) => !isDisplayColumnKind(column.columnDef.meta?.kind);

const getZoneColumns = <TData extends RowData>(
    table: DataGridTable<TData>,
    pin: DataGridPin | undefined,
) => {
    if (pin === "start") {
        return table.getStartLeafColumns();
    }
    return pin === "end"
        ? table.getEndLeafColumns()
        : table.getCenterLeafColumns();
};

const getZoneValueColumns = <TData extends RowData>(
    table: DataGridTable<TData>,
) =>
    ZONE_PINS.map((pin) => ({
        pin,
        columns: getZoneColumns(table, pin).filter(isValueColumn),
    }));

/** Value column ids by zone, in display order; the zones a drop reorders within. */
export const getZoneValueColumnIds = <TData extends RowData>(
    table: DataGridTable<TData>,
): string[][] =>
    getZoneValueColumns(table).map(({ columns }) =>
        columns.map(({ id }) => id),
    );

/**
 * Value columns by zone (start pins, unpinned, end pins), in display order.
 * `columnState` is the table's current column state, read as values so
 * callers recompute when it changes. The last shown column can't be hidden:
 * a saved view with no columns reads as the defaults.
 */
export const getColumnSettingsZones = <TData extends RowData>({
    table,
    columnState,
}: {
    table: DataGridTable<TData>;
    columnState: DataGridColumnState;
}): ColumnSettingsZone[] => {
    const zones = getZoneValueColumns(table);
    const isShown = (columnId: string) =>
        columnState.visibility[columnId] ?? true;
    const shownCount = zones
        .flatMap(({ columns }) => columns)
        .filter((column) => isShown(column.id)).length;
    return zones.map(({ pin, columns }) => ({
        pin,
        rows: columns.map((column) => {
            const isVisible = isShown(column.id);
            return {
                id: column.id,
                name: resolveColumnMeta(column.columnDef, column.id)
                    .headerLabel,
                isVisible,
                canHide:
                    column.getCanHide() && !(isVisible && shownCount === 1),
                pin,
            };
        }),
    }));
};

/** `order` completed the way TanStack reads it: missing columns go last, in definition order. */
const completeOrder = (
    order: readonly string[],
    definitionLeafIds: readonly string[],
) => {
    const ordered = new Set(order);
    return [
        ...order.filter((id) => definitionLeafIds.includes(id)),
        ...definitionLeafIds.filter((id) => !ordered.has(id)),
    ];
};

const getDefinitionLeafIds = <TData extends RowData>(
    table: DataGridTable<TData>,
) =>
    table
        .getAllFlatColumns()
        .filter((column) => column.columns.length === 0)
        .map(({ id }) => id);

/**
 * Every leaf id in the user's order. While grouped, TanStack's leaf getters
 * list grouped columns first; this is the order that gets saved.
 */
const getUserLeafIds = <TData extends RowData>(table: DataGridTable<TData>) =>
    completeOrder(table.atoms.columnOrder.get(), getDefinitionLeafIds(table));

/**
 * Moves a value column to `toIndex` among the value columns of its zone, as
 * displayed; display columns keep their slots. While grouped, unpinned grouped
 * columns show first whatever the order, so they don't move. The settings
 * list and header drags call it on drop.
 */
export const moveColumn = <TData extends RowData>(
    table: DataGridTable<TData>,
    { columnId, toIndex }: { columnId: string; toIndex: number },
) => {
    const column = table.getColumn(columnId);
    if (!column) {
        return;
    }
    const pin = getPin(column);
    const groupedIds = new Set(table.atoms.grouping.get());
    if (pin === undefined && groupedIds.has(columnId)) {
        return;
    }
    const zoneColumns = getZoneColumns(table, pin);
    const valueIds = zoneColumns.filter(isValueColumn).map(({ id }) => id);
    const fromIndex = valueIds.indexOf(columnId);
    if (
        fromIndex < 0 ||
        toIndex < 0 ||
        toIndex >= valueIds.length ||
        toIndex === fromIndex
    ) {
        return;
    }
    valueIds.splice(fromIndex, 1);
    valueIds.splice(toIndex, 0, columnId);
    let nextValue = 0;
    const zoneIds = zoneColumns.map((zoneColumn) =>
        isValueColumn(zoneColumn) ? valueIds[nextValue++] : zoneColumn.id,
    );
    if (pin !== undefined) {
        table.setColumnPinning((old) => ({ ...old, [pin]: zoneIds }));
        return;
    }
    const movedIds = zoneIds.filter((id) => !groupedIds.has(id));
    const movedIdSet = new Set(movedIds);
    let nextMoved = 0;
    table.setColumnOrder(
        getUserLeafIds(table).map((id) =>
            movedIdSet.has(id) ? movedIds[nextMoved++] : id,
        ),
    );
};

/**
 * Pins a column to the edge of the zone nearest the unpinned columns, so the
 * select and actions columns stay outermost; `undefined` unpins it.
 */
export const pinColumn = <TData extends RowData>(
    table: DataGridTable<TData>,
    { columnId, pin }: { columnId: string; pin: DataGridPin | undefined },
) => {
    table.setColumnPinning((old) => {
        const start = old.start.filter((id) => id !== columnId);
        const end = old.end.filter((id) => id !== columnId);
        if (pin === "start") {
            return { start: [...start, columnId], end };
        }
        return pin === "end"
            ? { start, end: [columnId, ...end] }
            : { start, end };
    });
};

/** A column whose shown variant differs from the one `defaults` shows. */
const hasVariantChange = <TData extends RowData>({
    column,
    variants,
    defaults,
}: {
    column: DataGridColumnInstance<TData>;
    variants: Record<string, string>;
    defaults: DataGridColumnState;
}) => {
    const columnVariants = column.columnDef.meta?.variants;
    return (
        columnVariants !== undefined &&
        getVariantValue(columnVariants, variants[column.id]) !==
            getVariantValue(columnVariants, defaults.variants[column.id])
    );
};

/** Whether order, visibility, widths, pins, or variants differ from `defaults`. */
export const hasColumnChanges = <TData extends RowData>({
    table,
    columnState: { visibility, sizing, pinning, variants },
    defaults,
}: {
    table: DataGridTable<TData>;
    columnState: DataGridColumnState;
    defaults: DataGridColumnState;
}) =>
    !isEqual(
        getUserLeafIds(table),
        completeOrder(defaults.order, getDefinitionLeafIds(table)),
    ) ||
    table
        .getAllLeafColumns()
        .some(
            (column) =>
                (visibility[column.id] ?? true) !==
                    (defaults.visibility[column.id] ?? true) ||
                (sizing[column.id] ?? column.columnDef.size) !==
                    (defaults.sizing[column.id] ?? column.columnDef.size) ||
                hasVariantChange({ column, variants, defaults }),
        ) ||
    !isEqual(pinning.start, defaults.pinning.start) ||
    !isEqual(pinning.end, defaults.pinning.end);

/** Back to the default order, visibility, widths, pins, and variants, as one change. */
export const resetColumns = <TData extends RowData>({
    table,
    columnVariants,
    defaults,
}: {
    table: DataGridTable<TData>;
    columnVariants: Atom<Record<string, string>>;
    defaults: DataGridColumnState;
}) => {
    batch(() => {
        table.setColumnOrder(defaults.order);
        table.setColumnVisibility(defaults.visibility);
        table.setColumnSizing(defaults.sizing);
        table.setColumnPinning(defaults.pinning);
        columnVariants.set(defaults.variants);
    });
};
